import Downshift from 'downshift';
import React from 'react';
import { CrossButton, ToggleDropdownArrow } from '../common/controller-buttons';
import { DropdownItem } from '../common/dropdown-item';
import { getFilteredItems, stringifyItem } from '../common/helpers';
import { IItem, ISelectProps } from '../common/interface';
import { MenuHeaderItems } from '../common/menu-header-items';
import { StyledInput, StyledWrapper } from '../search-dropdown.style';
import {
    DividerLine,
    StyledDesktopItem,
    StyledDesktopMenu,
    StyledHeaderItem,
    StyledSubHeaderItem,
} from './desktop-select.style';

const DesktopSelect = (props: ISelectProps) => {
    const allHeading: IItem = {
        key: '',
        title: props.heading,
    };

    const focusRef = React.useCallback((node: HTMLInputElement) => {
        if (node !== null) {
            node.focus();
        }
    }, []);
    return (
        <Downshift
            id="search autocomplete"
            itemToString={(item) => {
                if (typeof item === 'string') return item;
                else if (item === null || item === undefined) return '';
                return stringifyItem(item);
            }}
            stateReducer={props.stateReducer}
            onSelect={props.onSelectHandler}
            // provides option for the dropdown to be controlled and uncontrolled
            {...(props.selectedItem !== undefined && { selectedItem: props.selectedItem })}
        >
            {({
                getInputProps,
                getToggleButtonProps,
                getMenuProps,
                getItemProps,
                isOpen,
                selectedItem,
                inputValue,
                highlightedIndex,
                openMenu,
                clearSelection,
            }) => (
                <div>
                    <StyledWrapper>
                        {!isOpen ? (
                            <StyledInput
                                aria-label={`select ${props.heading}`}
                                {...getInputProps({
                                    onFocus: openMenu,
                                    onClick: openMenu,
                                    isOpen,
                                    'data-testid': `${props.category}-select`,
                                })}
                                value={selectedItem ? stringifyItem(selectedItem) : props.heading}
                                style={{ cursor: 'pointer' }}
                                readOnly
                            />
                        ) : (
                            <StyledInput
                                {...getInputProps({
                                    onFocus: openMenu,
                                    isOpen,
                                    placeholder: selectedItem
                                        ? stringifyItem(selectedItem)
                                        : props.placeholder,
                                    style: {
                                        opacity: 1,
                                        boxShadow: '0 0 6px 0 rgba(0, 0, 0, 0.7)',
                                        height: '44px',
                                    },
                                    'data-testid': `${props.category}-select`,
                                })}
                                ref={focusRef}
                                aria-label={`select ${props.heading}`}
                                data-testid-value={props.selectedItem?.key || null}
                            />
                        )}
                        {selectedItem && !isOpen ? (
                            <CrossButton
                                onClickHandler={() => {
                                    clearSelection();
                                    openMenu();
                                }}
                                ariaLabel="clear selection"
                            />
                        ) : (
                            <ToggleDropdownArrow
                                getToggleButtonProps={getToggleButtonProps}
                                isOpen={isOpen}
                            />
                        )}
                    </StyledWrapper>
                    <StyledWrapper>
                        {isOpen ? <DividerLine /> : null}
                        <StyledDesktopMenu {...getMenuProps()} menuOpen={isOpen}>
                            {isOpen ? (
                                <>
                                    {!inputValue && (
                                        <MenuHeaderItems
                                            highlightedIndex={highlightedIndex}
                                            getItemProps={getItemProps}
                                            allHeadingItem={allHeading}
                                            selectedItem={selectedItem}
                                            heading={props.heading}
                                            menuItems={props.items.top}
                                            inputValue={inputValue}
                                            ItemComponent={StyledDesktopItem}
                                            HeaderItemComponent={StyledHeaderItem}
                                            SubHeaderItemComponent={StyledSubHeaderItem}
                                        />
                                    )}
                                    {props?.items?.all?.length &&
                                        getFilteredItems(inputValue, props.items.all).map(
                                            (item, index) => {
                                                const filteredItems =
                                                    getFilteredItems(
                                                        inputValue,
                                                        props?.items?.top,
                                                    ) ?? [];
                                                const offsetIndex =
                                                    index +
                                                    (!inputValue ? filteredItems.length + 1 : 0);
                                                return (
                                                    <DropdownItem
                                                        key={offsetIndex}
                                                        index={offsetIndex}
                                                        highlightedIndex={highlightedIndex}
                                                        getItemProps={getItemProps}
                                                        item={item}
                                                        selectedItem={selectedItem}
                                                        ItemComponent={StyledDesktopItem}
                                                    />
                                                );
                                            },
                                        )}
                                </>
                            ) : null}
                        </StyledDesktopMenu>
                    </StyledWrapper>
                </div>
            )}
        </Downshift>
    );
};

export default DesktopSelect;
